import React, { createContext, useContext, ReactNode } from 'react';
import { useCapabilities } from '@hooks/useCapabilities';

// Define the capabilities context type
interface CapabilitiesContextType {
  // Core capability checking
  hasCapability: (capability: string) => boolean;
  hasAnyCapability: (capabilities: string[]) => boolean;
  hasAllCapabilities: (capabilities: string[]) => boolean;

  // Role checking
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;

  // Data access
  getCapabilities: () => string[];
  getRoles: () => string[];

  // Specific capability checks
  canViewOffers: () => boolean;
  canCreateOffers: () => boolean;
  canUpdateOffers: () => boolean;
  canDeleteOffers: () => boolean;
  canViewCompanySettings: () => boolean;
  canUpdateCompanySettings: () => boolean;
  canManageUsers: () => boolean;
  canManageSSO: () => boolean;
  canViewValuations: () => boolean;
  canUpdateValuations: () => boolean;
  canSubmitFeedback: () => boolean;
  canViewWhatsNew: () => boolean;
}

// Create the context
const CapabilitiesContext = createContext<CapabilitiesContextType | null>(null);

// Custom hook for easy use of the context
export const useCapabilitiesContext = () => {
  const context = useContext(CapabilitiesContext);
  if (context === null) {
    throw new Error('useCapabilitiesContext must be used within a CapabilitiesProvider');
  }
  return context;
};

// Props for the CapabilitiesProvider component
interface CapabilitiesProviderProps {
  children: ReactNode;
}

// CapabilitiesProvider component
export const CapabilitiesProvider: React.FC<CapabilitiesProviderProps> = ({ children }) => {
  const capabilities = useCapabilities();

  return (
    <CapabilitiesContext.Provider value={capabilities}>{children}</CapabilitiesContext.Provider>
  );
};
